import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface PageChromeBack {
  to: string;
  label?: string;
  ariaLabel?: string;
}

export interface PageChromeValue {
  back?: PageChromeBack;
  title?: string;
  docs?: string;
  description?: string;
}

export type ChromeContributor = (id: symbol, value: PageChromeValue | null) => void;

export const PageChromeStateContext = createContext<PageChromeValue | null>(null);
export const PageChromeDispatchContext = createContext<ChromeContributor | null>(null);

export function mergeChromeSources(sources: Map<symbol, PageChromeValue>): PageChromeValue | null {
  if (sources.size === 0) return null;
  const merged: PageChromeValue = {};
  for (const value of sources.values()) {
    if (value.back) merged.back = value.back;
    if (value.title !== undefined) merged.title = value.title;
    if (value.docs !== undefined) merged.docs = value.docs;
    if (value.description !== undefined) merged.description = value.description;
  }
  return merged;
}

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<PageChromeValue | null>(null);
  const sourcesRef = useRef(new Map<symbol, PageChromeValue>());
  const contribute = useCallback<ChromeContributor>((id, value) => {
    if (value) sourcesRef.current.set(id, value);
    else sourcesRef.current.delete(id);
    setChrome(mergeChromeSources(sourcesRef.current));
  }, []);

  return (
    <PageChromeDispatchContext.Provider value={contribute}>
      <PageChromeStateContext.Provider value={chrome}>{children}</PageChromeStateContext.Provider>
    </PageChromeDispatchContext.Provider>
  );
}

/** Current chrome payload. `null` outside a takeover or when nothing is registered. */
export function usePageChrome(): PageChromeValue | null {
  return useContext(PageChromeStateContext);
}

/**
 * Publish nested-page / hub chrome into the takeover mobile header.
 * No-ops outside `PageChromeProvider` so pages still render without a shell.
 * Returns whether a chrome slot is active (so callers can hide inline copy).
 * Multiple callers merge: hubs contribute title, `PageHeader` contributes
 * docs / description, nested headers contribute back. Plugin pages use the
 * shared `PageHeader`, so they must register here rather than in web.
 */
export function useRegisterPageChrome(value: PageChromeValue): boolean {
  const contribute = useContext(PageChromeDispatchContext);
  const idRef = useRef(Symbol('page-chrome'));
  const backTo = value.back?.to;
  const backLabel = value.back?.label;
  const backAriaLabel = value.back?.ariaLabel;
  const { title, docs, description } = value;

  useEffect(() => {
    if (!contribute) return;
    const id = idRef.current;
    contribute(id, {
      back: backTo ? { to: backTo, label: backLabel, ariaLabel: backAriaLabel } : undefined,
      title,
      docs,
      description,
    });
    return () => contribute(id, null);
  }, [contribute, backTo, backLabel, backAriaLabel, title, docs, description]);

  return contribute != null;
}
